import { Box, Chip } from '@mui/material';
import { useTheme } from '@mui/material/styles';

import type { AcademyConfirmedBy } from '../../../pages/academy/Academy/AcademyService';
import { getAcademyMaskedIconStyle } from '../Utilities/academyMaskedIcon';
import {
  getConfirmationLabel,
  getSkillProgressState,
  isConfirmed,
} from '../Utilities/academyLabels';
import { academyStatusClassNames, academyStatusStyles } from './AcademyStatus.styles';
import checkRoundedIconUrl from './Images/check-rounded.svg';

const skillProgressDotClasses = {
  complete: 'full',
  partially_complete: 'half',
  not_complete: 'blank',
} as const;

export function StatusBadge({
  label,
  confirmedBy,
}: {
  label: string;
  confirmedBy: AcademyConfirmedBy;
}) {
  const theme = useTheme();

  return (
    <Chip
      component="span"
      className={academyStatusClassNames.badge}
      sx={academyStatusStyles.badge(theme)}
      aria-label={`${label} ${getConfirmationLabel(confirmedBy)}`}
      label={
        <>
          {label}
          <ConfirmationDot confirmedBy={confirmedBy} />
        </>
      }
      variant="outlined"
    />
  );
}

export function StatusDot({
  learnConfirmedBy,
  applyConfirmedBy,
  learnRequired = true,
  applyRequired = true,
}: {
  learnConfirmedBy: AcademyConfirmedBy;
  applyConfirmedBy: AcademyConfirmedBy;
  learnRequired?: boolean;
  applyRequired?: boolean;
}) {
  const progressClass =
    skillProgressDotClasses[
      getSkillProgressState(learnConfirmedBy, applyConfirmedBy, learnRequired, applyRequired)
    ];

  return (
    <Box
      component="span"
      className={`${academyStatusClassNames.dot} ${progressClass}`}
      sx={academyStatusStyles.dot()}
      aria-hidden="true"
    >
      {progressClass === 'full' ? <CheckIcon /> : null}
    </Box>
  );
}

function ConfirmationDot({ confirmedBy }: { confirmedBy: AcademyConfirmedBy }) {
  const isComplete = isConfirmed(confirmedBy);

  return (
    <Box
      component="span"
      className={`${academyStatusClassNames.dot} ${isComplete ? 'full' : 'blank'}`}
      sx={academyStatusStyles.dot()}
      aria-hidden="true"
    >
      {isComplete ? <CheckIcon /> : null}
    </Box>
  );
}

export function CheckIcon() {
  return (
    <Box
      component="span"
      className={academyStatusClassNames.checkIcon}
      sx={academyStatusStyles.checkIcon()}
      style={getAcademyMaskedIconStyle(checkRoundedIconUrl)}
      aria-hidden="true"
    />
  );
}
